import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  setFixerId,
  setAddress,
  setSelectedShop,
} from '../../../actions/course';
import buildSupportFormUrl from '../../../utils/supportFormUrl';
// Course components
// Shared components
import Row from '../../shared/atoms/Row';
import Box from '../../shared/atoms/Box';
import Button from '../../shared/atoms/Button';
import Separator from '../../Separator';
import Text from '../../shared/atoms/Text';
import Link from '../../shared/atoms/Link';
// Icons
import Time16 from '../../icons/Time16';
import Back16 from '../../icons/Back16';
import ThinkingFaceColor56 from '../../icons/ThinkingFaceColor56';
import HelpColor56 from '../../icons/HelpColor56';
import Messaging56Color from '../../icons/Messaging56Color';
import BrandIcon from './BrandIcon';
import * as api from '../../../utils/api';
import { RJ, TITLE_FONT } from '../../rapport/rapportTokens';
import isCompanyCourse from '../../../utils/companyCourse';

// "Rapport de visite disponible !" card — replaces the legacy devis-rebook
// row at the top of the finished-state sidebar. Visual sibling of the
// per-velo FutureQuoteBlock cards in the main column.
const RapportRecoCard = styled(Box)`
  border: 1px solid ${RJ.border};
  border-radius: ${RJ.radiusLg};
  padding: 16px;
  background: ${RJ.white};
  margin-bottom: 12px;
`;

const RapportRecoBadge = styled.span`
  /* Compact pill — kept small so the icon stays the visual anchor.
     line-height:1 + symmetric padding so the uppercase glyphs sit
     visually centred (default line-height adds asymmetric leading
     above uppercase caps). */
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 10px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${(p) => p.theme.colors.violet.pale};
  color: ${(p) => p.theme.colors.violet.dark};
  white-space: nowrap;
`;

// Plain flex row so the brand icon keeps its intrinsic shape. The shared
// Row atom forces `flex: 1` on children (unless noSize), which stretches
// the icon into an ellipse alongside the badge.
const RecoHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const RapportRecoTitle = styled(Text)`
  font-weight: 700;
  font-size: 18px;
  color: ${RJ.text};
  margin: 12px 0 8px;
`;

const RapportRecoDescription = styled(Text)`
  color: ${(p) => p.theme.colors.gray.darker};
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 14px;
`;

// §5.6 — "Que faire maintenant ?" : a titled #f8f9fa container (identical on
// desktop sticky-right and stacked mobile) holding separate white action cards.
const ActionsPanel = styled.div`
  background: ${RJ.bg};
  border: 1px solid ${RJ.border};
  border-radius: ${RJ.radiusCard};
  padding: 16px;
`;

const ActionsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;

  & svg {
    flex-shrink: 0;
  }

  & > span {
    font-family: ${TITLE_FONT};
    font-weight: 900;
    font-size: 18px;
    color: ${RJ.text};
  }
`;

const ActionCard = styled.div`
  background: ${RJ.white};
  border: 1px solid ${RJ.border};
  border-radius: ${RJ.radiusLg};
  padding: 16px;

  & + & {
    margin-top: 12px;
  }
`;

const SideActions = ({ course, openModal }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const clientEmail = useSelector((s) => s.client?.email);
  const isShop = !!course.fixer?.shop_id;
  const supportUrl = buildSupportFormUrl({ course, clientEmail });

  switch (course.state) {
    case 'dispatched':
      return (
        <Box
          borderColor="gray.light"
          borderWidth="1px"
          borderRadius="sm"
          borderStyle="solid"
          p={4}>
          <Row md gap={16}>
            <Box>
              <Text>{t('sideactions.changedyourmind')}</Text>
              <Button
                before={<Time16 />}
                size="compact"
                variant="neutral"
                color="primary.darkest"
                fullWidth
                mt={2}
                onClick={() => openModal('CANCEL_COURSE')}>
                {t('cancelrequest.cta')}
              </Button>
            </Box>
          </Row>
          <Separator axe="horizontal" mt={4} />
          <Row md gap={16}>
            <HelpColor56 />
            {isShop ? (
              <Box>
                <Text>
                  En cas de problème veuillez contacter directement l'atelier
                </Text>
                <Link
                  color="blue.electric"
                  onClick={() =>
                    window.open(
                      `/ateliers-reparation/${course.fixer?.shop_id}/${course.fixer.prenom}`,
                      '_blank',
                    )
                  }>
                  Voir la fiche atelier
                </Link>
              </Box>
            ) : (
              <Box>
                <Text>{t('sideactions.needhelp')}</Text>
                <Link
                  color="blue.electric"
                  onClick={() =>
                    window.open('https://faq.roulezjeunesse.com/', '_blank')
                  }>
                  {t('sideactions.gethelp')}
                </Link>
                <Link
                  color="blue.electric"
                  onClick={() => window.open(supportUrl, '_blank')}>
                  {t('contactus')}
                </Link>
              </Box>
            )}
          </Row>
          <Separator axe="horizontal" mt={4} />
          <Row md gap={16}>
            <Messaging56Color />
            <Box>
              <Text>{t('sideactions.dispatched.text')}</Text>
            </Box>
          </Row>
        </Box>
      );

    case 'scheduled':
      return (
        <Box
          borderColor="gray.light"
          borderWidth="1px"
          borderRadius="sm"
          borderStyle="solid"
          p={4}>
          <Row md gap={16}>
            {/* <ThinkingFaceColor56 /> */}
            <Box>
              <Text>{t('sideactions.changedyourmind')}</Text>
              <Button
                variant="neutral"
                size="compact"
                fullWidth
                mt={2}
                onClick={() =>
                  openModal(
                    'UPDATE_DISPONIBILITY',
                    isShop
                      ? { shop_id: course.fixer?.shop_id }
                      : { fixer_id: course.fixer_id },
                  )
                }>
                {t('sideactions.scheduled.reschedule')}
              </Button>
              {!isShop && (
                <Button
                  variant="neutral"
                  size="compact"
                  fullWidth
                  mt={2}
                  onClick={() => openModal('UPDATE_ADRESSE')}>
                  {t('sideactions.scheduled.changeadress')}
                </Button>
              )}
              <Button
                before={<Time16 />}
                variant="neutral"
                color="primary.darkest"
                fullWidth
                mt={2}
                size="compact"
                onClick={() => openModal('CANCEL_COURSE')}>
                {t('cancelrequest.cta')}
              </Button>
            </Box>
          </Row>
          <Separator axe="horizontal" mt={4} />
          <Row md gap={16}>
            <HelpColor56 />
            {isShop ? (
              <Box>
                <Text>
                  En cas de problème veuillez contacter directement l'atelier
                </Text>
                <Link
                  color="blue.electric"
                  onClick={() =>
                    window.open(
                      `/ateliers-reparation/${course.fixer?.shop_id}/${course.fixer.prenom}`,
                      '_blank',
                    )
                  }>
                  Voir la fiche atelier
                </Link>
              </Box>
            ) : (
              <Box>
                <Text>{t('sideactions.needhelp')}</Text>
                <Link
                  color="blue.electric"
                  onClick={() =>
                    window.open('https://faq.roulezjeunesse.com/', '_blank')
                  }>
                  {t('sideactions.gethelp')}
                </Link>
                <Link
                  color="blue.electric"
                  onClick={() => window.open(supportUrl, '_blank')}>
                  {t('contactus')}
                </Link>
              </Box>
            )}
          </Row>
        </Box>
      );
    case 'finished': {
      const hasPublishedReport =
        course.visit_report && course.visit_report.published_at;
      const reportToken = hasPublishedReport ? course.visit_report.token : null;
      return (
        <ActionsPanel data-testid="sideactions-whatnow">
          <ActionsTitle>
            <BrandIcon name="que-faire" size={28} />
            <span>{t('sideactions.whatnow.title')}</span>
          </ActionsTitle>
          {hasPublishedReport && reportToken && (
            <RapportRecoCard data-testid="sideactions-rapport-reco-card">
              <RecoHeaderRow>
                <BrandIcon name="facture" size={40} />
                <RapportRecoBadge>
                  {t('sideactions.rapport.badge')}
                </RapportRecoBadge>
              </RecoHeaderRow>
              <RapportRecoTitle>
                {t('sideactions.rapport.title')}
              </RapportRecoTitle>
              <RapportRecoDescription>
                {t('sideactions.rapport.description', {
                  fixerName: course.fixer && course.fixer.prenom,
                })}
              </RapportRecoDescription>
              <Button
                as="a"
                href={`/reparation/depuis-devis/${encodeURIComponent(
                  reportToken,
                )}/recap`}
                fullWidth
                mb={2}
                data-testid="sideactions-devisbooking-cta">
                {t('sideactions.rapport.cta.plan')}
              </Button>
              <Button
                as="a"
                href={`/rapport/${encodeURIComponent(reportToken)}`}
                variant="outlinedBlack"
                fullWidth
                data-testid="sideactions-rapport-cta">
                {t('sideactions.rapport.cta.view')}
              </Button>
            </RapportRecoCard>
          )}
          <ActionCard>
            <Row md gap={16}>
              <BrandIcon
                name="reprendre-rdv"
                size={52}
                style={{ flex: '0 0 auto' }}
              />
              <Box>
                <Text>
                  {t('sideactions.finished.newappointment', {
                    label: course.fixer.prenom,
                  })}
                </Text>
                <Box mt={2}>
                  <Link
                    color="violet.main"
                    onClick={
                      isShop
                        ? async () => {
                            const shop = await api.get(
                              `/shops/${course.fixer.shop_id}`,
                            );
                            dispatch(setSelectedShop(shop));
                            history.push('/reparation/symptoms');
                          }
                        : () => {
                            dispatch(setFixerId(course.fixer.id));
                            history.push('/reparation');
                          }
                    }>
                    {t('sideactions.finished.newappointment.cta', {
                      label: course.fixer.prenom,
                    })}
                  </Link>
                </Box>
              </Box>
            </Row>
          </ActionCard>
          <ActionCard>
            <Row md gap={16}>
              <BrandIcon name="avis" size={52} style={{ flex: '0 0 auto' }} />
              <Box>
                <Text>{t('sideactions.finished.feedback')}</Text>
                <Box mt={2}>
                  <Link
                    color="violet.main"
                    onClick={() =>
                      window.open(
                        `https://feedback.roulezjeunesse.com/satisfaction-post-intervention?course_id=${course.id}&fixer=${course.fixer.prenom}&fixer_id=${course.fixer.id}`,
                        '_blank',
                      )
                    }>
                    {t('sideactions.finished.feedback.cta')}
                  </Link>
                </Box>
              </Box>
            </Row>
          </ActionCard>
          <ActionCard>
            <Row md gap={16}>
              <BrandIcon name="aide" size={52} style={{ flex: '0 0 auto' }} />
              {isShop ? (
                <Box>
                  <Text>
                    En cas de problème veuillez contacter directement l'atelier
                  </Text>
                  <Link
                    color="violet.main"
                    onClick={() =>
                      window.open(
                        `/ateliers-reparation/${course.fixer?.shop_id}/${course.fixer.prenom}`,
                        '_blank',
                      )
                    }>
                    Voir la fiche atelier
                  </Link>
                </Box>
              ) : (
                <Box>
                  <Text>{t('sideactions.needhelp')}</Text>
                  <Link
                    color="violet.main"
                    onClick={() =>
                      window.open('https://faq.roulezjeunesse.com/', '_blank')
                    }>
                    {t('sideactions.gethelp')}
                  </Link>
                  <Link
                    color="violet.main"
                    onClick={() => window.open(supportUrl, '_blank')}>
                    {t('contactus')}
                  </Link>
                </Box>
              )}
            </Row>
          </ActionCard>
        </ActionsPanel>
      );
    }
    default:
      return (
        <Box
          borderColor="gray.light"
          borderWidth="1px"
          borderRadius="sm"
          borderStyle="solid"
          p={4}>
          <Row md gap={16}>
            <ThinkingFaceColor56 />
            {isCompanyCourse(course) ? (
              // FIX-929 — pas de relance en ligne d'une intervention B2B.
              <Box>
                <Text>{t('relaunchrequest.company')}</Text>
              </Box>
            ) : (
              <Box>
                <Text>{t('sideactions.default.texts.1')}</Text>
                <Button
                  color="blue.electric"
                  before={<Back16 />}
                  fullWidth
                  size="compact"
                  mt={2}
                  onClick={() =>
                    openModal('UPDATE_DISPONIBILITY', {
                      isReopen: true,
                    })
                  }>
                  {t('relaunchrequest')}
                </Button>
              </Box>
            )}
          </Row>
          <Separator axe="horizontal" mt={4} />
          <Row md gap={16}>
            <HelpColor56 />
            {isShop ? (
              <Box>
                <Text>
                  En cas de problème veuillez contacter directement l'atelier
                </Text>
                <Link
                  color="blue.electric"
                  onClick={() =>
                    window.open(
                      `/ateliers-reparation/${course.fixer?.shop_id}/${course.fixer.prenom}`,
                      '_blank',
                    )
                  }>
                  Voir la fiche atelier
                </Link>
              </Box>
            ) : (
              <Box>
                <Text>{t('sideactions.needhelp')}</Text>
                <Link
                  color="blue.electric"
                  onClick={() =>
                    window.open('https://faq.roulezjeunesse.com/', '_blank')
                  }>
                  {t('sideactions.gethelp')}
                </Link>
                <Link
                  color="blue.electric"
                  onClick={() => window.open(supportUrl, '_blank')}>
                  {t('contactus')}
                </Link>
              </Box>
            )}
          </Row>
        </Box>
      );
  }
};

export default SideActions;
